import React, { useRef, useEffect } from 'react';
import { VariableSizeList as List } from 'react-window';
import MessageThread from './MessageThread';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  timestamp: string;
  sequenceId: number;
  metadata?: any;
  isOptimistic?: boolean;
  replyToMessageId?: string;
  isDeleted?: boolean;
  readReceipts?: { [key: string]: string };
}

interface VirtualizedMessageListProps {
  messages: Message[];
  conversationId: string;
  onReply: (messageId: string) => void;
  height: number;
  width: number;
}

/**
 * Virtualized message list using react-window for performance optimization
 * Only renders visible messages in the viewport, dramatically improving performance
 * for conversations with 100+ messages
 */
const VirtualizedMessageList: React.FC<VirtualizedMessageListProps> = ({
  messages,
  conversationId,
  onReply,
  height,
  width,
}) => {
  const listRef = useRef<List>(null);
  const rowHeights = useRef<{ [key: string]: number }>({});

  // Filter top-level messages (no replies)
  const topLevelMessages = messages.filter(m => !m.replyToMessageId);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (listRef.current && topLevelMessages.length > 0) {
      listRef.current.scrollToItem(topLevelMessages.length - 1, 'end');
    }
  }, [topLevelMessages.length]);

  // Get row height from cache or estimate
  const getRowHeight = (index: number) => {
    const message = topLevelMessages[index];

    // Return cached height if available
    if (rowHeights.current[message.id]) {
      return rowHeights.current[message.id];
    }

    // Estimate height based on content length
    const baseHeight = 80; // Base height for avatar + padding
    const lineHeight = 24; // Approximate line height
    const charsPerLine = 60; // Approximate characters per line
    const contentLines = Math.ceil(message.content.length / charsPerLine);
    const estimatedHeight = baseHeight + (contentLines * lineHeight);

    // Cache the estimate
    rowHeights.current[message.id] = estimatedHeight;

    return estimatedHeight;
  };

  // Set exact height when component is measured
  const setRowHeight = (index: number, size: number) => {
    const message = topLevelMessages[index];

    if (rowHeights.current[message.id] !== size) {
      rowHeights.current[message.id] = size;

      // Force re-render with updated heights
      if (listRef.current) {
        listRef.current.resetAfterIndex(index);
      }
    }
  };

  // Row renderer
  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const message = topLevelMessages[index];
    const rowRef = useRef<HTMLDivElement>(null);

    // Measure actual height after render
    useEffect(() => {
      if (rowRef.current) {
        const height = rowRef.current.getBoundingClientRect().height;
        setRowHeight(index, height);
      }
    }, [index, message.content]);

    return (
      <div style={style} ref={rowRef} role="listitem">
        <div className="px-6 py-2">
          <MessageThread
            message={message}
            allMessages={messages}
            onReply={onReply}
            conversationId={conversationId}
          />
        </div>
      </div>
    );
  };

  if (topLevelMessages.length === 0) {
    return (
      <div
        className="flex items-center justify-center h-full text-gray-400"
        style={{ height, width }}
        role="status"
        aria-label="No messages"
      >
        <p>No messages yet. Start the conversation!</p>
      </div>
    );
  }

  return (
    <div role="list" aria-label="Message list">
      <List
        ref={listRef}
        height={height}
        width={width}
        itemCount={topLevelMessages.length}
        itemSize={getRowHeight}
        overscanCount={5}
      >
        {Row}
      </List>
    </div>
  );
};

export default VirtualizedMessageList;
